import type { FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AppShell,
  Button,
  ButtonLink,
  DataTable,
  EmptyState,
  Field,
  MetricStrip,
  PageHeader,
  ProgressMeter,
  StatusPill,
  Surface,
  TurnstileChallenge
} from "@moxipass/ui";
import { api, idempotencyKey } from "./api";

const nav = [
  { href: "/", label: "Overview", icon: "solar:chart-2-bold-duotone" },
  { href: "/setup", label: "Setup", icon: "solar:shop-bold-duotone" },
  { href: "/qr", label: "QR", icon: "solar:qr-code-bold-duotone" },
  { href: "/members", label: "Members", icon: "solar:users-group-rounded-bold-duotone" },
  { href: "/campaigns", label: "Campaigns", icon: "solar:letter-bold-duotone" },
  { href: "/staff", label: "Staff mode", icon: "solar:scanner-bold-duotone" },
  { href: "/billing", label: "Billing", icon: "solar:bill-list-bold-duotone" }
];

const DEFAULT_MERCHANT_ID = "mer_01jz0000000000000000000000";
const DEFAULT_BRANCH_ID = "br_01jz0000000000000000000000";
const DEFAULT_PROGRAMME_ID = "prog_01jz000000000000000000003";
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

function route() {
  return `/${window.location.pathname.split("/").filter(Boolean)[0] ?? ""}`;
}

export function App() {
  const activeRoute = route();
  const activeNav = nav.map((item) => ({ ...item, active: item.href === activeRoute || (item.href === "/" && activeRoute === "/") }));

  return (
    <AppShell appName="MoxiPass" eyebrow="Vendor PWA" nav={activeNav}>
      {activeRoute === "/" ? <Overview /> : null}
      {activeRoute === "/setup" ? <Setup /> : null}
      {activeRoute === "/qr" ? <QrMaterials /> : null}
      {activeRoute === "/members" ? <Members /> : null}
      {activeRoute === "/campaigns" ? <Campaigns /> : null}
      {activeRoute === "/staff" ? <StaffMode /> : null}
      {activeRoute === "/billing" ? <Billing /> : null}
    </AppShell>
  );
}

function Overview() {
  const overview = useQuery({
    queryKey: ["vendor", "overview"],
    queryFn: api.overview
  });
  const merchantName = overview.data?.merchant.name ?? "Merchant";
  const metrics = overview.data?.metrics ?? { members: 0, pointsEarnedToday: 0, redemptionsToday: 0, campaignsPublished: 0 };

  return (
    <>
      <PageHeader title={`${merchantName} operations`} kicker="Merchant workspace">
        <ButtonLink href="/staff" icon="solar:scanner-bold-duotone">Open staff mode</ButtonLink>
      </PageHeader>
      <MetricStrip
        items={[
          { label: "Members", value: metrics.members, detail: "Active memberships", tone: "blue" },
          { label: "Earned today", value: metrics.pointsEarnedToday, detail: "Points issued", tone: "green" },
          { label: "Redeemed", value: metrics.redemptionsToday, detail: "Confirmed today", tone: "amber" },
          { label: "Campaigns", value: metrics.campaignsPublished, detail: "Published", tone: "blue" }
        ]}
      />
      <Surface title="Setup progress" icon="solar:checklist-minimalistic-bold-duotone">
        <ProgressMeter value={overview.data?.setupProgress ?? 0} max={100} label="Launch readiness" />
      </Surface>
      <section className="grid-two">
        <Surface title="Next actions" icon="solar:bolt-bold-duotone">
          <ul className="list">
            <li className="list-row"><span>Download counter QR</span><StatusPill tone="amber">Pending</StatusPill></li>
            <li className="list-row"><span>Invite cashier account</span><StatusPill tone="green">Ready</StatusPill></li>
            <li className="list-row"><span>Run first wallet inbox campaign</span><StatusPill tone="blue">Optional</StatusPill></li>
          </ul>
        </Surface>
        <Surface title="Risk guardrails" icon="solar:shield-check-bold-duotone">
          <ul className="list">
            <li className="list-row"><span>Offline earn/redeem</span><strong>Blocked</strong></li>
            <li className="list-row"><span>Redemption token TTL</span><strong>4 minutes</strong></li>
            <li className="list-row"><span>Ledger source of truth</span><strong>Enabled</strong></li>
          </ul>
        </Surface>
      </section>
    </>
  );
}

function Setup() {
  const merchant = useMutation({ mutationFn: api.createMerchant });
  const programme = useMutation({ mutationFn: api.createProgramme });
  const reward = useMutation({ mutationFn: api.createReward });
  const logo = useMutation({ mutationFn: api.uploadLogo });
  const setupMerchantId = merchant.data?.merchantId ?? DEFAULT_MERCHANT_ID;
  const setupProgrammeId = programme.data?.programmeId ?? merchant.data?.programmeId ?? DEFAULT_PROGRAMME_ID;

  function submitMerchant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.currentTarget));
    merchant.mutate({ ...form, plan: form.plan ?? "starter" });
  }

  function submitProgramme(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.currentTarget));
    programme.mutate({
      merchantId: form.merchantId || DEFAULT_MERCHANT_ID,
      type: form.type,
      name: form.name,
      earnRate: Number(form.earnRate),
      earnUnit: form.earnUnit,
      expiryDays: Number(form.expiryDays),
      idempotencyKey: idempotencyKey("programme")
    });
  }

  function submitLogo(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    logo.mutate(form);
  }

  function submitReward(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.currentTarget));
    reward.mutate({
      merchantId: form.merchantId || setupMerchantId,
      programmeId: form.programmeId || setupProgrammeId,
      name: form.name,
      description: form.description,
      pointsCost: Number(form.pointsCost),
      status: form.status,
      redemptionLimit: Number(form.redemptionLimit) || undefined,
      idempotencyKey: idempotencyKey("reward")
    });
  }

  return (
    <>
      <PageHeader title="Merchant setup" kicker="Onboarding" />
      <section className="grid-two">
        <Surface title="Merchant profile" icon="solar:shop-bold-duotone">
          <form className="form-grid" onSubmit={submitMerchant}>
            <Field label="Name"><input name="name" defaultValue="Baan Cha" /></Field>
            <Field label="Slug"><input name="slug" defaultValue="baan-cha-new" /></Field>
            <Field label="Category"><input name="category" defaultValue="Thai milk tea" /></Field>
            <Field label="Owner email"><input name="ownerEmail" type="email" defaultValue="owner@baancha.example" /></Field>
            <Field label="Plan"><select name="plan" defaultValue="starter"><option value="starter">Starter</option><option value="growth">Growth</option><option value="manual">Manual</option></select></Field>
            <TurnstileChallenge siteKey={TURNSTILE_SITE_KEY} action="vendor_signup" />
            <Button type="submit" icon="solar:add-circle-bold-duotone" disabled={merchant.isPending}>Create merchant</Button>
          </form>
          {merchant.data ? (
            <p className="tone-green">
              Created {merchant.data.merchantId} with reward {merchant.data.rewardId} and QR {merchant.data.qrToken}
            </p>
          ) : null}
        </Surface>
        <Surface title="Loyalty programme" icon="solar:medal-star-bold-duotone">
          <form className="form-grid" onSubmit={submitProgramme} key={setupMerchantId}>
            <Field label="Merchant ID"><input name="merchantId" defaultValue={setupMerchantId} /></Field>
            <Field label="Template"><select name="type" defaultValue="points"><option value="points">Points</option><option value="stamp">Stamp</option><option value="coupon">Coupon only</option></select></Field>
            <Field label="Programme name"><input name="name" defaultValue="Baan Cha Points" /></Field>
            <Field label="Earn rate"><input name="earnRate" type="number" defaultValue="1" /></Field>
            <Field label="Earn unit"><input name="earnUnit" defaultValue="25 THB" /></Field>
            <Field label="Expiry days"><input name="expiryDays" type="number" defaultValue="365" /></Field>
            <Button type="submit" icon="solar:settings-bold-duotone" disabled={programme.isPending}>Save programme</Button>
          </form>
          {programme.data ? <p className="tone-green">Programme {programme.data.programmeId} active</p> : null}
        </Surface>
        <Surface title="Reward" icon="solar:gift-bold-duotone">
          <form className="form-grid" onSubmit={submitReward} key={`${setupMerchantId}:${setupProgrammeId}`}>
            <Field label="Merchant ID"><input name="merchantId" defaultValue={setupMerchantId} /></Field>
            <Field label="Programme ID"><input name="programmeId" defaultValue={setupProgrammeId} /></Field>
            <Field label="Reward name"><input name="name" defaultValue="Signature drink" /></Field>
            <Field label="Description"><textarea name="description" defaultValue="Any regular drink at the counter." /></Field>
            <Field label="Point cost"><input name="pointsCost" type="number" defaultValue="120" /></Field>
            <Field label="Status"><select name="status" defaultValue="active"><option value="active">Active</option><option value="paused">Paused</option></select></Field>
            <Field label="Limit"><input name="redemptionLimit" type="number" placeholder="Optional" /></Field>
            <Button type="submit" icon="solar:gift-bold-duotone" disabled={reward.isPending}>Publish reward</Button>
          </form>
          {reward.data ? <p className="tone-green">Reward {reward.data.rewardId} {reward.data.status}</p> : null}
        </Surface>
        <Surface title="Logo asset" icon="solar:gallery-add-bold-duotone">
          <form className="form-grid" onSubmit={submitLogo} key={`logo:${setupMerchantId}`}>
            <Field label="Merchant ID"><input name="merchantId" defaultValue={setupMerchantId} /></Field>
            <Field label="Logo"><input name="logo" type="file" accept="image/png,image/jpeg,image/webp" /></Field>
            <Button type="submit" icon="solar:upload-minimalistic-bold-duotone" disabled={logo.isPending}>Upload logo</Button>
          </form>
          {logo.data ? <p className="tone-green">Uploaded {logo.data.objectKey}</p> : null}
        </Surface>
      </section>
    </>
  );
}

function QrMaterials() {
  const qr = useQuery({
    queryKey: ["vendor", "qr"],
    queryFn: api.qrMaterials
  });
  const materials = qr.data;

  return (
    <>
      <PageHeader title="QR materials" kicker="Counter assets" />
      <section className="grid-two">
        <Surface title="Counter QR" icon="solar:qr-code-bold-duotone">
          <div className="qr-block"><span>{materials?.merchantSlug ?? "QR"}</span></div>
          <p className="muted">{materials?.counterUrl ?? "Loading counter QR link"}</p>
        </Surface>
        <Surface title="Share links" icon="solar:link-bold-duotone">
          <div className="detail-row"><span>Poster</span><strong>{materials?.posterUrl ?? "Loading"}</strong></div>
          <div className="detail-row"><span>Share</span><strong>{materials?.shareUrl ?? "Loading"}</strong></div>
        </Surface>
      </section>
    </>
  );
}

function Members() {
  const members = useQuery({ queryKey: ["vendor", "members"], queryFn: api.members });
  const rows = members.data?.members ?? [];

  return (
    <>
      <PageHeader title="Members and CRM" kicker="Customer list" />
      <Surface title="Segments" icon="solar:users-group-rounded-bold-duotone">
        {rows.length ? (
          <DataTable
            columns={["Member", "Balance", "Segment", "Last activity"]}
            rows={rows.map((item) => [
              item.customerId,
              `${item.balance} pts`,
              item.segment === "vip" ? (
                <StatusPill tone="green">VIP</StatusPill>
              ) : (
                <StatusPill tone={item.segment === "repeat" ? "blue" : "neutral"}>{item.segment}</StatusPill>
              ),
              new Date(item.lastActivityAt).toLocaleString()
            ])}
          />
        ) : (
          <EmptyState icon="solar:users-group-rounded-line-duotone" title="No members yet" body="Joined customers will appear here." />
        )}
      </Surface>
    </>
  );
}

function Campaigns() {
  const campaign = useMutation({ mutationFn: api.createCampaign });
  const rewards = useQuery({ queryKey: ["vendor", "rewards"], queryFn: api.rewards });

  function submitCampaign(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.currentTarget));
    campaign.mutate({
      merchantId: DEFAULT_MERCHANT_ID,
      title: form.title,
      body: form.body,
      audience: form.audience,
      idempotencyKey: idempotencyKey("campaign")
    });
  }

  return (
    <>
      <PageHeader title="Wallet inbox campaigns" kicker="Campaigns" />
      <section className="grid-two">
        <Surface title="Create campaign" icon="solar:letter-bold-duotone">
          <form className="form-grid" onSubmit={submitCampaign}>
            <Field label="Template"><select name="template" defaultValue="rainy"><option value="rainy">Rainy day</option><option value="inactive">Inactive comeback</option><option value="birthday">Birthday</option></select></Field>
            <Field label="Audience"><select name="audience" defaultValue="all"><option value="all">All members</option><option value="repeat">Repeat</option><option value="vip">VIP</option><option value="inactive">Inactive</option></select></Field>
            <Field label="Title"><input name="title" defaultValue="Rainy day double points" /></Field>
            <Field label="Message"><textarea name="body" defaultValue="Earn double points today from 14:00 to 17:00 at the counter." /></Field>
            <Button type="submit" icon="solar:plain-bold-duotone" disabled={campaign.isPending}>Publish</Button>
          </form>
          {campaign.data ? <p className="tone-green">Campaign {campaign.data.campaignId} queued</p> : null}
        </Surface>
        <Surface title="Published rewards" icon="solar:gift-bold-duotone">
          {(rewards.data ?? []).length ? (
            <ul className="list">
              {(rewards.data ?? []).map((reward) => (
                <li className="list-row" key={reward.id}>
                  <span>{reward.name}</span>
                  <strong>{reward.pointsCost} pts</strong>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon="solar:gift-line-duotone" title="No rewards" body="Published rewards will appear here." />
          )}
        </Surface>
      </section>
    </>
  );
}

function StaffMode() {
  const queryClient = useQueryClient();
  const add = useMutation({ mutationFn: api.addPoints });
  const redeem = useMutation({ mutationFn: api.confirmRedemption });
  const actions = useQuery({ queryKey: ["staff", "actions"], queryFn: api.recentActions, placeholderData: [] });
  const staff = useQuery({ queryKey: ["vendor", "staff"], queryFn: api.staffUsers, placeholderData: { staff: [] } });
  const actionRows = actions.data ?? [];
  const staffRows = staff.data?.staff ?? [];
  const invite = useMutation({
    mutationFn: api.inviteStaff,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["vendor", "staff"] })
  });
  const staffStatus = useMutation({
    mutationFn: ({ staffUserId, status }: { staffUserId: string; status: "active" | "disabled" }) =>
      api.updateStaffStatus(staffUserId, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["vendor", "staff"] })
  });

  function submitPoints(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.currentTarget));
    add.mutate({
      merchantId: DEFAULT_MERCHANT_ID,
      branchId: DEFAULT_BRANCH_ID,
      customerId: form.customerId,
      membershipId: form.membershipId,
      programmeId: form.programmeId,
      pointsDelta: Number(form.pointsDelta),
      billAmount: Number(form.billAmount),
      idempotencyKey: idempotencyKey("points")
    });
  }

  function submitInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.currentTarget));
    invite.mutate({
      merchantId: DEFAULT_MERCHANT_ID,
      email: form.email,
      displayName: form.displayName,
      role: form.role,
      idempotencyKey: idempotencyKey("staff")
    });
  }

  function submitRedemption(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.currentTarget));
    redeem.mutate({
      merchantId: DEFAULT_MERCHANT_ID,
      branchId: DEFAULT_BRANCH_ID,
      redemptionId: form.redemptionId,
      redemptionToken: form.redemptionToken,
      staffUserId: "usr_01jz0000000000000000000000",
      idempotencyKey: idempotencyKey("redemption-confirm")
    });
  }

  return (
    <>
      <PageHeader title="Staff mode" kicker="Counter flow">
        <StatusPill tone={navigator.onLine ? "green" : "red"}>{navigator.onLine ? "Online" : "Offline"}</StatusPill>
      </PageHeader>
      <section className="grid-two">
        <Surface title="Add points" icon="solar:scanner-bold-duotone">
          <form className="form-grid" onSubmit={submitPoints}>
            <Field label="Customer ID"><input name="customerId" defaultValue="cus_01jz0000000000000000000001" /></Field>
            <Field label="Membership ID"><input name="membershipId" defaultValue="mem_01jz0000000000000000000002" /></Field>
            <Field label="Programme ID"><input name="programmeId" defaultValue={DEFAULT_PROGRAMME_ID} /></Field>
            <Field label="Bill amount"><input name="billAmount" type="number" defaultValue="300" /></Field>
            <Field label="Points"><input name="pointsDelta" type="number" defaultValue="12" /></Field>
            <Button type="submit" icon="solar:add-circle-bold-duotone" disabled={add.isPending || !navigator.onLine}>Submit online</Button>
          </form>
          {add.data ? <p className="tone-green">Ledger {add.data.ledgerId}. New balance {add.data.balance}</p> : null}
          {add.error ? <p className="tone-red">{add.error.message}</p> : null}
        </Surface>
        <Surface title="Redeem reward" icon="solar:ticket-sale-bold-duotone">
          <form className="form-grid" onSubmit={submitRedemption}>
            <Field label="Redemption ID"><input name="redemptionId" placeholder="red_..." /></Field>
            <Field label="Redemption token"><input name="redemptionToken" placeholder="Customer code" /></Field>
            <Button type="submit" icon="solar:verified-check-bold-duotone" disabled={redeem.isPending || !navigator.onLine}>Confirm online</Button>
          </form>
          {redeem.data ? <p className="tone-green">Redeemed {redeem.data.redemptionId}. New balance {redeem.data.balance}</p> : null}
          {redeem.error ? <p className="tone-red">{redeem.error.message}</p> : null}
        </Surface>
        <Surface title="Recent actions" icon="solar:history-bold-duotone">
          <ul className="list">
            {actionRows.map((action) => (
              <li className="list-row" key={String(action.id)}>
                <span>{String(action.action)}</span>
                <strong>{String(action.pointsDelta ?? "")}</strong>
              </li>
            ))}
          </ul>
        </Surface>
        <Surface title="Staff management" icon="solar:users-group-rounded-bold-duotone">
          <form className="form-grid" onSubmit={submitInvite}>
            <Field label="Email"><input name="email" type="email" defaultValue="cashier@baancha.example" /></Field>
            <Field label="Name"><input name="displayName" defaultValue="Counter cashier" /></Field>
            <Field label="Role">
              <select name="role" defaultValue="cashier">
                <option value="cashier">Cashier</option>
                <option value="manager">Manager</option>
                <option value="viewer">Viewer</option>
              </select>
            </Field>
            <Button type="submit" icon="solar:user-plus-bold-duotone" disabled={invite.isPending}>Invite staff</Button>
          </form>
          {invite.data ? <p className="tone-green">{invite.data.email} is {invite.data.status}</p> : null}
          <ul className="list">
            {staffRows.map((member) => (
              <li className="list-row" key={member.id}>
                <div>
                  <strong>{member.displayName}</strong>
                  <span>{member.email} · {member.role}</span>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  icon={member.status === "disabled" ? "solar:restart-bold-duotone" : "solar:forbidden-circle-bold-duotone"}
                  onClick={() => staffStatus.mutate({ staffUserId: member.id, status: member.status === "disabled" ? "active" : "disabled" })}
                  disabled={staffStatus.isPending}
                >
                  {member.status === "disabled" ? "Activate" : "Disable"}
                </Button>
              </li>
            ))}
          </ul>
        </Surface>
      </section>
    </>
  );
}

function Billing() {
  const queryClient = useQueryClient();
  const session = useQuery({ queryKey: ["auth", "vendor-session"], queryFn: api.session, retry: false });
  const login = useMutation({
    mutationFn: ({ email, turnstileToken }: { email: string; turnstileToken?: string }) => api.login(email, "owner", turnstileToken)
  });
  const logout = useMutation({ mutationFn: api.logout });
  const exports = useQuery({ queryKey: ["vendor", "exports"], queryFn: api.exports, placeholderData: { exports: [] } });
  const exportRows = exports.data?.exports ?? [];
  const requestExport = useMutation({
    mutationFn: () => api.requestExport({ merchantId: DEFAULT_MERCHANT_ID, idempotencyKey: idempotencyKey("export") }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["vendor", "exports"] })
  });

  function submitLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    login.mutate({
      email: String(form.get("email") ?? ""),
      turnstileToken: String(form.get("turnstileToken") ?? "")
    });
  }

  return (
    <>
      <PageHeader title="Billing and account status" kicker="Manual billing V1" />
      <Surface title="Vendor session" icon="solar:login-3-bold-duotone">
        <form className="form-grid" onSubmit={submitLogin}>
          <Field label="Owner email"><input name="email" type="email" defaultValue="owner@baancha.example" /></Field>
          <TurnstileChallenge siteKey={TURNSTILE_SITE_KEY} action="vendor_login" />
          <Button type="submit" icon="solar:login-3-bold-duotone" disabled={login.isPending}>Sign in</Button>
          <Button type="button" variant="secondary" icon="solar:logout-3-bold-duotone" onClick={() => logout.mutate()} disabled={logout.isPending}>Logout</Button>
        </form>
        <div className="detail-row">
          <span>Current session</span>
          <strong>{login.data?.role ?? session.data?.role ?? "No active session"}</strong>
        </div>
      </Surface>
      <Surface title="Plan status" icon="solar:bill-list-bold-duotone">
        <div className="detail-row"><span>Plan</span><strong>Starter</strong></div>
        <div className="detail-row"><span>Status</span><StatusPill tone="green">Active</StatusPill></div>
        <div className="detail-row"><span>Renewal</span><strong>2026-06-24</strong></div>
      </Surface>
      <Surface title="Data export" icon="solar:archive-down-minimlistic-bold-duotone">
        <Button type="button" icon="solar:download-minimalistic-bold-duotone" onClick={() => requestExport.mutate()} disabled={requestExport.isPending}>
          Request export
        </Button>
        <ul className="list">
          {exportRows.map((item) => (
            <li className="list-row" key={item.id}>
              <div>
                <strong>{item.id}</strong>
                <span>{new Date(item.createdAt).toLocaleString()}</span>
              </div>
              {item.status === "ready" ? (
                <ButtonLink href={api.exportDownloadUrl(item.id)} variant="secondary" icon="solar:download-minimalistic-bold-duotone">
                  CSV
                </ButtonLink>
              ) : (
                <StatusPill tone={item.status === "failed" ? "red" : "amber"}>{item.status}</StatusPill>
              )}
            </li>
          ))}
        </ul>
      </Surface>
    </>
  );
}
